import { createFileRoute, notFound } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import {
  AiActionTray,
  type AiSuggestionAction,
  type FieldEditAction,
  type ProjectMetadataAction,
  type ProjectTaskAction,
} from '@/components/docfiller/ai-actions'
import { Button } from '@/components/ui/button'

/**
 * Dev-only preview surface for the AI suggestion review tray. Mounted
 * under three scenarios (single, batch, error/conflict) so we can
 * snapshot deterministic screenshots without booting the live agent.
 *
 * Reachable only at `/dev/ai-suggestions-preview` in dev builds —
 * production builds 404 the route.
 */
export const Route = createFileRoute('/dev/ai-suggestions-preview')({
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound()
  },
  component: AiSuggestionsPreview,
})

const RUN_ID = '01HF7Z9R3W9X9YQZ8K3F2EXAMPLE'

const SINGLE_EDIT: FieldEditAction = {
  kind: 'field-edit',
  id: 'single-1',
  documentUuid: 'doc-uuid',
  fieldUuid: 'field-new-address',
  fieldDisplayName: 'New home mailing address',
  fieldRawName: 'TOPMOSTSUBFORM[0].Page1[0].NewAddress[0]',
  fieldKind: 'text',
  before: '742 Evergreen Terrace, Springfield, OR 97477',
  after: '15 Mission St #3B, San Francisco, CA 94105',
  context: {
    runId: RUN_ID,
    confidence: 0.96,
    reasoning:
      'Quoted directly from the user instructions: "moving to 15 Mission St in SF".',
    source: 'Agent (chat)',
  },
}

const BATCH_EDITS: FieldEditAction[] = [
  {
    kind: 'field-edit',
    id: 'batch-1',
    documentUuid: 'doc-uuid',
    fieldUuid: 'field-first-name',
    fieldDisplayName: 'Your first name',
    fieldRawName: 'TOPMOSTSUBFORM[0].Page1[0].YourFirstName[0]',
    fieldKind: 'text',
    before: null,
    after: 'Alice',
    context: {
      runId: RUN_ID,
      confidence: 0.97,
      reasoning: 'Pulled from the workspace profile.',
      source: 'Agent (chat)',
    },
  },
  {
    kind: 'field-edit',
    id: 'batch-2',
    documentUuid: 'doc-uuid',
    fieldUuid: 'field-last-name',
    fieldDisplayName: 'Your last name',
    fieldRawName: 'TOPMOSTSUBFORM[0].Page1[0].YourLastName[0]',
    fieldKind: 'text',
    before: null,
    after: 'Nguyen',
    context: {
      runId: RUN_ID,
      confidence: 0.97,
      reasoning: 'Pulled from the workspace profile.',
      source: 'Agent (chat)',
    },
  },
  {
    kind: 'field-edit',
    id: 'batch-3',
    documentUuid: 'doc-uuid',
    fieldUuid: 'field-ssn',
    fieldDisplayName: 'Your social security number',
    fieldRawName: 'TOPMOSTSUBFORM[0].Page1[0].YourSSN[0]',
    fieldKind: 'text',
    before: '111-22-3333',
    after: '123-45-6789',
    context: {
      runId: RUN_ID,
      confidence: 0.42,
      reasoning: 'Reconstructed from a screenshot — please verify.',
      source: 'Agent (chat)',
    },
  },
  {
    kind: 'field-edit',
    id: 'batch-4',
    documentUuid: 'doc-uuid',
    fieldUuid: 'field-tax-checkbox',
    fieldDisplayName: 'Individual income tax returns',
    fieldRawName: 'TOPMOSTSUBFORM[0].Page1[0].Box1_TaxReturns[0]',
    fieldKind: 'checkbox',
    before: 'false',
    after: 'true',
    context: {
      runId: RUN_ID,
      confidence: 0.91,
      reasoning: "User noted they're updating their 1040 mailing address.",
      source: 'Agent (chat)',
    },
  },
]

const PROJECT_META: ProjectMetadataAction = {
  kind: 'project-metadata',
  id: 'proj-meta-1',
  workspaceUuid: 'ws-uuid',
  projectUuid: 'proj-uuid',
  projectName: 'Q3 Tax Returns',
  changes: {
    name: { before: 'Q3 Tax Returns', after: 'Q3 2026 Tax Returns' },
    description: {
      before: null,
      after:
        'Working folder for the Q3 2026 individual + small-business tax returns. Drop completed 1040s and supporting schedules here.',
    },
  },
  context: {
    confidence: 0.88,
    reasoning:
      'Picked up the year from the uploaded W-2 cover page; cleaned up the description from the user-supplied brief.',
    source: 'Agent (setup)',
  },
}

const PROJECT_TASKS: ProjectTaskAction[] = [
  {
    kind: 'project-task',
    id: 'proj-task-1',
    workspaceUuid: 'ws-uuid',
    projectUuid: 'proj-uuid',
    taskTitle: 'Upload all W-2 / 1099 PDFs',
    taskDescription:
      'Per the workspace brief, collect every W-2 and 1099 from 2026 employers and drop them in the "Inputs" folder before running autofill.',
    context: {
      confidence: 0.93,
      reasoning: 'Inferred from the project brief.',
      source: 'Agent (setup)',
    },
  },
  {
    kind: 'project-task',
    id: 'proj-task-2',
    workspaceUuid: 'ws-uuid',
    projectUuid: 'proj-uuid',
    taskTitle: 'Confirm dependent SSNs',
    taskDescription:
      'Verify each dependent SSN against the prior-year return before letting the agent fill new forms.',
    context: {
      confidence: 0.72,
      reasoning: 'Recommended given dependent-claim discrepancies in prior runs.',
      source: 'Agent (setup)',
    },
  },
]

function AiSuggestionsPreview() {
  return (
    <div
      className="mx-auto max-w-5xl space-y-8 p-6"
      data-testid="ai-suggestions-preview"
    >
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">
          AI suggestion tray — review states
        </h1>
        <p className="max-w-2xl text-sm text-muted-foreground">
          Every state the tray can render. The same component drives the
          Suggestions tab on a real document, the project setup wizard, and
          any future explicit AI action surface.
        </p>
      </header>

      <Section
        id="single"
        title="Single edit"
        subtitle="One AI-suggested document field change with diff + context."
      >
        <ScenarioSingleEdit />
      </Section>

      <Section
        id="batch"
        title="Batch edits"
        subtitle="Multiple suggestions with select-all + Apply N. Low-confidence rows get a visible warning."
      >
        <ScenarioBatchEdit />
      </Section>

      <Section
        id="error-conflict"
        title="Error + conflict + permission denied"
        subtitle="One card fails, one collides with a fresh edit, one is forbidden. Tray surfaces each state explicitly without rolling back the others."
      >
        <ScenarioErrors />
      </Section>

      <Section
        id="project-setup"
        title="Project setup"
        subtitle="Same tray, this time wrapping project metadata + task suggestions."
      >
        <ScenarioProjectSetup />
      </Section>
    </div>
  )
}

function Section({
  id,
  title,
  subtitle,
  children,
}: {
  id: string
  title: string
  subtitle: string
  children: React.ReactNode
}) {
  return (
    <section className="space-y-2" data-section={id}>
      <div>
        <h2 className="text-base font-semibold text-foreground">{title}</h2>
        <p className="text-xs leading-relaxed text-muted-foreground">
          {subtitle}
        </p>
      </div>
      {children}
    </section>
  )
}

function ScenarioSingleEdit() {
  const [resetKey, setResetKey] = useState(0)
  return (
    <div className="space-y-2">
      <AiActionTray
        key={resetKey}
        actions={[SINGLE_EDIT]}
        onApply={fakeOk}
        onReject={fakeOk}
        title="One field-edit suggestion"
        description="The user can see exactly what will change. Nothing is applied until they click Apply."
      />
      <ResetButton onClick={() => setResetKey((k) => k + 1)} />
    </div>
  )
}

function ScenarioBatchEdit() {
  const [resetKey, setResetKey] = useState(0)
  return (
    <div className="space-y-2">
      <AiActionTray
        key={resetKey}
        actions={BATCH_EDITS}
        onApply={fakeOk}
        onReject={fakeOk}
        title="Four pending field edits"
        description="Use the per-row checkboxes (or Select all) to apply several at once."
      />
      <ResetButton onClick={() => setResetKey((k) => k + 1)} />
    </div>
  )
}

function ScenarioErrors() {
  const [resetKey, setResetKey] = useState(0)
  const actions = BATCH_EDITS.slice(0, 3)
  const initialStates = useMemo(
    () => ({
      [actions[0]!.id]: {
        status: 'conflict' as const,
        message:
          'Value changed since the suggestion was made. Current: "Alicia".',
        conflictCurrent: 'Alicia',
      },
      [actions[1]!.id]: {
        status: 'failed' as const,
        message: 'API returned 500 — try again or dismiss.',
      },
      [actions[2]!.id]: {
        status: 'denied' as const,
        message: 'Viewers cannot apply AI edits on this document.',
      },
    }),
    [actions],
  )
  return (
    <div className="space-y-2">
      <AiActionTray
        key={resetKey}
        actions={actions}
        initialStates={initialStates}
        onApply={fakeOk}
        onReject={fakeOk}
        title="Three problem cases at once"
        description="Conflict, server error, and permission denied — each card carries its own state without poisoning the others."
      />
      <ResetButton onClick={() => setResetKey((k) => k + 1)} />
    </div>
  )
}

function ScenarioProjectSetup() {
  const [resetKey, setResetKey] = useState(0)
  const actions: AiSuggestionAction[] = [PROJECT_META, ...PROJECT_TASKS]
  return (
    <div className="space-y-2">
      <AiActionTray
        key={resetKey}
        actions={actions}
        onApply={fakeOk}
        onReject={fakeOk}
        title="Project setup suggestions"
        description="Rename the project, add a description, and seed initial tasks — only after the user explicitly accepts."
      />
      <ResetButton onClick={() => setResetKey((k) => k + 1)} />
    </div>
  )
}

function ResetButton({ onClick }: { onClick: () => void }) {
  return (
    <div className="flex justify-end">
      <Button size="sm" variant="ghost" onClick={onClick}>
        Reset scenario
      </Button>
    </div>
  )
}

async function fakeOk() {
  await Promise.resolve()
}
